import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MessageThread = ({ conversation, messages, onSendMessage, onRefresh }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage?.trim()) return;

    try {
      setSending(true);
      await onSendMessage({
        clientId: conversation?.client?.id,
        communicationType: 'chat',
        subject: 'Message instantané',
        message: newMessage,
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTypeIcon = (type) => {
    const icons = {
      email: 'Mail',
      phone: 'Phone',
      meeting: 'Calendar',
      chat: 'MessageSquare',
    };
    return icons?.[type] || 'MessageSquare';
  };

  const getTypeLabel = (type) => {
    const labels = {
      email: 'Email',
      phone: 'Appel téléphonique',
      meeting: 'Réunion',
      chat: 'Message',
    };
    return labels?.[type] || type;
  };

  return (
    <div className="bg-card rounded-xl border border-border flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {conversation?.client?.avatar_url ? (
              <img
                src={conversation?.client?.avatar_url}
                alt={conversation?.client?.full_name || 'Client'}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Icon name="User" size={20} className="text-primary" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {conversation?.client?.full_name || 'Client'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {conversation?.client?.email}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <Icon name="RefreshCw" size={16} />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Icon name="MessageSquare" size={48} className="text-muted-foreground opacity-20 mx-auto mb-3" />
              <p className="text-muted-foreground">Aucun message</p>
            </div>
          </div>
        ) : (
          messages?.map((message, index) => {
            const showDate = index === 0 || 
              new Date(messages?.[index - 1]?.createdAt)?.toDateString() !== 
              new Date(message?.createdAt)?.toDateString();

            return (
              <div key={message?.id}>
                {/* Date Separator */}
                {showDate && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-muted px-3 py-1 rounded-full">
                      <p className="text-xs text-muted-foreground">
                        {formatDate(message?.createdAt)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <Icon name={getTypeIcon(message?.communicationType)} size={16} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {message?.user?.full_name || 'Équipe ORAZEK'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(message?.createdAt)}
                      </span>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                        {getTypeLabel(message?.communicationType)}
                      </span>
                    </div>
                    {message?.subject && (
                      <p className="text-sm font-medium text-foreground mb-1">
                        {message?.subject}
                      </p>
                    )}
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {message?.message}
                    </p>
                    {message?.attachments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message?.attachments?.map((attachment, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg text-sm"
                          >
                            <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                            <span className="text-foreground">{attachment?.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            placeholder="Tapez votre message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSend()}
            disabled={sending}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage?.trim()}>
            {sending ? (
              <Icon name="Loader" size={20} className="animate-spin" />
            ) : (
              <Icon name="Send" size={20} />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Appuyez sur Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne
        </p>
      </div>
    </div>
  );
};

export default MessageThread;
